import { IceServerResponse, SdpAnswerRequest, SdpOfferRequest, SdpOfferResponse, WebRTCErrorType, WebRTCServerReply } from '../types/webrtc';

export class WebRTCServiceError extends Error {
  constructor(public type: WebRTCErrorType, message: string) {
    super(message);
    this.name = 'WebRTCServiceError';
  }
}

const logger = {
  info: (...args: any[]) => console.log('[WebRTC]', ...args),
  warn: (...args: any[]) => console.warn('[WebRTC]', ...args),
  error: (...args: any[]) => console.error('[WebRTC]', ...args),
};

export async function getIceServers(timeoutMs: number = 10000): Promise<IceServerResponse> {
  const serviceUrl = import.meta.env.VITE_AI_SERVICE_URL;
  const url = serviceUrl ? `${serviceUrl}/ice` : '/ice';
  
  console.log('[WebRTC] getIceServers - Starting', {
    serviceUrl,
    url,
    timeoutMs,
    windowLocation: window.location.href,
  });
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.error('[WebRTC] getIceServers - Timeout triggered after', timeoutMs, 'ms');
    controller.abort();
  }, timeoutMs);

  try {
    console.log('[WebRTC] getIceServers - Fetching', url);
    const fetchStartTime = Date.now();
    const response = await fetch(url, {
      signal: controller.signal,
    });
    const fetchDuration = Date.now() - fetchStartTime;
    console.log('[WebRTC] getIceServers - Fetch completed', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      duration: `${fetchDuration}ms`,
    });
    
    clearTimeout(timeoutId);
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read response');
      console.error('[WebRTC] getIceServers - Response not OK', {
        status: response.status,
        statusText: response.statusText,
        errorText,
      });
      throw new WebRTCServiceError(WebRTCErrorType.SIGNALING_ERROR, `Failed to retrieve ICE servers: ${response.statusText}`);
    }
    const data = await response.json();
    console.log('[WebRTC] getIceServers - Response data received', {
      hasIceServers: !!data.iceServers,
      iceServersCount: Array.isArray(data.iceServers) ? data.iceServers.length : 0,
      hasSessionId: !!data.session_id,
      sessionId: data.session_id,
    });
    if (!data.iceServers || !Array.isArray(data.iceServers) || !data.session_id) {
      console.error('[WebRTC] getIceServers - Invalid response format', data);
      throw new WebRTCServiceError(WebRTCErrorType.SIGNALING_ERROR, 'Invalid ICE servers format received');
    }
    console.log('[WebRTC] getIceServers - Success');
    return data;
  } catch (error: any) {
    clearTimeout(timeoutId);
    console.error('[WebRTC] getIceServers - Error caught', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      isAbortError: error.name === 'AbortError',
      isWebRTCServiceError: error instanceof WebRTCServiceError,
    });
    if (error.name === 'AbortError') {
      throw new WebRTCServiceError(WebRTCErrorType.CONNECTION_FAILED, 'ICE server retrieval timed out');
    }
    if (error instanceof WebRTCServiceError) throw error;
    throw new WebRTCServiceError(WebRTCErrorType.CONNECTION_FAILED, `Network error retrieving ICE servers: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export function connectSignalingWebSocket(sessionId: string): WebSocket {
  let socketUrl: string;
  const serviceUrl = import.meta.env.VITE_AI_SERVICE_URL;
  
  if (serviceUrl) {
    // Determine the correct WebSocket protocol based on current page protocol
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    
    // Convert HTTP(S) URL to WebSocket URL
    // Handle both http:// and https:// properly
    let finalWsUrl: string;
    if (serviceUrl.startsWith('https://')) {
      // Already HTTPS, use wss://
      finalWsUrl = serviceUrl.replace(/^https:\/\//, 'wss://');
    } else if (serviceUrl.startsWith('http://')) {
      // HTTP, convert to ws:// or wss:// based on current page protocol
      finalWsUrl = serviceUrl.replace(/^http:\/\//, `${protocol}://`);
    } else {
      // Not an HTTP(S) URL, assume it's already a WebSocket URL or use relative
      finalWsUrl = serviceUrl.startsWith('ws://') || serviceUrl.startsWith('wss://')
        ? serviceUrl
        : `${protocol}://${window.location.host}${serviceUrl.startsWith('/') ? '' : '/'}${serviceUrl}`;
    }
    
    socketUrl = `${finalWsUrl}/ws?session_id=${sessionId}`;
  } else {
    // Use relative path for proxy
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    socketUrl = `${protocol}://${window.location.host}/ws?session_id=${sessionId}`;
  }

  const ws = new WebSocket(socketUrl);
  
  ws.onerror = (event) => {
    logger.error('WebSocket signaling error:', event);
  };
  
  return ws;
}

export class SignalingChannel {
  private ws: WebSocket | null = null;
  private sessionId: string;
  private initialReconnectTimeout: number = 1000;
  private reconnectTimeout: number = 1000;
  private maxReconnectTimeout: number = 30000;
  private isClosing: boolean = false;

  constructor(sessionId: string) {
    this.sessionId = sessionId;
    this.reconnectTimeout = this.initialReconnectTimeout;
  }

  connect() {
    this.isClosing = false;
    this.ws = connectSignalingWebSocket(this.sessionId);
    
    this.ws.onopen = () => {
      logger.info(`WebSocket connected for session ${this.sessionId}`);
      this.reconnectTimeout = this.initialReconnectTimeout; // Reset on success
    };

    this.ws.onclose = () => {
      if (!this.isClosing) {
        this.handleReconnect();
      }
    };
  }

  private handleReconnect() {
    setTimeout(() => {
      logger.info(`Attempting to reconnect WebSocket for session ${this.sessionId}...`);
      this.connect();
      // Exponential backoff
      this.reconnectTimeout = Math.min(this.reconnectTimeout * 2, this.maxReconnectTimeout);
    }, this.reconnectTimeout);
  }

  send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      logger.warn('WebSocket is not open. Cannot send message.');
    }
  }

  close() {
    this.isClosing = true;
    if (this.ws) {
      this.ws.close();
    }
  }

  get socket() {
    return this.ws;
  }
}

export async function fetchOffer(
  sessionId: string, 
  restaurantId: string, 
  tableId: string, 
  userId?: string, 
  timeoutMs: number = 10000
): Promise<RTCSessionDescriptionInit> {
  const payload: SdpOfferRequest = {
    session_id: sessionId,
    restaurant_id: restaurantId,
    table_id: tableId,
    user_id: userId,
  };

  const serviceUrl = import.meta.env.VITE_AI_SERVICE_URL;
  const url = serviceUrl ? `${serviceUrl}/offer` : '/offer';

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new WebRTCServiceError(WebRTCErrorType.SIGNALING_ERROR, `Failed to fetch offer: ${response.statusText}`);
    }

    const data: SdpOfferResponse = await response.json();
    if (!data.sdp) {
      throw new WebRTCServiceError(WebRTCErrorType.SIGNALING_ERROR, 'Invalid offer format received from server');
    }
    
    return {
      type: 'offer',
      sdp: data.sdp,
    };
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new WebRTCServiceError(WebRTCErrorType.CONNECTION_FAILED, 'SDP offer retrieval timed out');
    }
    if (error instanceof WebRTCServiceError) throw error;
    throw new WebRTCServiceError(WebRTCErrorType.CONNECTION_FAILED, `Network error fetching offer: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export async function sendAnswer(
  sessionId: string, 
  answer: RTCSessionDescriptionInit, 
  timeoutMs: number = 10000
): Promise<void> {
  const payload: SdpAnswerRequest = {
    session_id: sessionId,
    sdp: answer.sdp || '',
    type: 'answer',
  };

  const serviceUrl = import.meta.env.VITE_AI_SERVICE_URL;
  const url = serviceUrl ? `${serviceUrl}/answer` : '/answer';

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new WebRTCServiceError(WebRTCErrorType.SIGNALING_ERROR, `Failed to send answer: ${response.statusText}`);
    }
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new WebRTCServiceError(WebRTCErrorType.CONNECTION_FAILED, 'SDP answer exchange timed out');
    }
    if (error instanceof WebRTCServiceError) throw error;
    throw new WebRTCServiceError(WebRTCErrorType.CONNECTION_FAILED, `Network error sending answer: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export function sendIceCandidate(ws: WebSocket, candidate: RTCIceCandidate): void {
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({
      type: 'ICE',
      candidate: {
        candidate: candidate.candidate,
        sdpMid: candidate.sdpMid,
        sdpMLineIndex: candidate.sdpMLineIndex,
      },
    }));
  }
}

export class IceCandidateBuffer {
  private candidates: RTCIceCandidateInit[] = [];
  private isRemoteDescriptionSet = false;

  constructor(private pc: RTCPeerConnection) {}

  addCandidate(candidate: RTCIceCandidateInit) {
    if (this.isRemoteDescriptionSet) {
      this.pc.addIceCandidate(candidate).catch(e => logger.error('Error adding ICE candidate:', e));
    } else {
      this.candidates.push(candidate);
    }
  }

  processBufferedCandidates() {
    this.isRemoteDescriptionSet = true;
    while (this.candidates.length > 0) {
      const candidate = this.candidates.shift();
      if (candidate) {
        this.pc.addIceCandidate(candidate).catch(e => logger.error('Error adding buffered ICE candidate:', e));
      }
    }
  }
}

export function setupDataChannel(pc: RTCPeerConnection, onMessage: (data: WebRTCServerReply) => void): RTCDataChannel {
  const dc = pc.createDataChannel('messages', {
    ordered: true,
  });

  dc.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as WebRTCServerReply;
      onMessage(data);
    } catch (e) {
      logger.error('Error parsing DataChannel message:', e);
    }
  };

  return dc;
}
